/* eslint-disable react/require-default-props */
import React from 'react';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import { FilterToggle } from '../../molecules/Filter/FilterToggle';
import { BooleanFilter, CheckboxFilter, DateRangeFilter, MultiSelectFilter, MultiSelectFilterWithAvatar, PagedMultiSelectFilter, SelectFilter, TextFilter, TreeMultiSelectFilter, TreeSelectFilter } from '../../molecules/Filter';
import { assertNever } from './assertNever';
import './FilterBarStyles.scss';
const namespace = 'ui.filter_bar';
/**
 * Resolve a FilterBar copy key, falling back when Sprockets i18n-js is stale
 * or the key is absent. i18n-js returns `[missing "en...."]` — not the bare
 * key — so comparing only to `key`/`namespace.key` is not enough.
 */
function translateWithFallback(key, fallback) {
    const translated = translate(key, { namespace });
    const fullKey = `${namespace}.${key}`;
    if (translated === key ||
        translated === fullKey ||
        translated.startsWith('[missing "') ||
        translated.startsWith("[missing '")) {
        return fallback;
    }
    return translated;
}
function isFilterVisible(filter) {
    return filter.active !== false;
}
function renderFilter(filter) {
    // `locked` only means "cannot be turned off in Manage filters" (FilterToggle).
    // It must not disable the chip — IP-15825 Sales Person was unusable because of that mapping.
    const disabled = false;
    switch (filter.type) {
        case 'boolean':
            return (React.createElement(BooleanFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, trueLabel: filter.trueLabel, falseLabel: filter.falseLabel, allLabel: filter.allLabel, persistAllSelection: filter.persistAllSelection, disabled: disabled }));
        case 'checkbox':
            return (React.createElement(CheckboxFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, disabled: disabled }));
        case 'text':
            return (React.createElement(TextFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, debounceMs: filter.debounceMs, placeholder: filter.placeholder, disabled: disabled }));
        case 'select':
            return (React.createElement(SelectFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, options: filter.options, disabled: disabled }));
        case 'multiSelect':
            return (React.createElement(MultiSelectFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, options: filter.options, placeholder: filter.placeholder, disabled: disabled }));
        case 'multiSelectWithAvatar':
            return (React.createElement(MultiSelectFilterWithAvatar, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, options: filter.options, placeholder: filter.placeholder, disabled: disabled, includeInactive: filter.includeInactive, onIncludeInactiveChange: filter.onIncludeInactiveChange, includeInactiveLabel: filter.includeInactiveLabel }));
        case 'pagedMultiSelect':
            return (React.createElement(PagedMultiSelectFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, loadOptions: filter.loadOptions, loadSelectedValues: filter.loadSelectedValues, placeholder: filter.placeholder, disabled: disabled }));
        case 'treeSelect':
            return (React.createElement(TreeSelectFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, options: filter.options, placeholder: filter.placeholder, disabled: disabled }));
        case 'treeMultiSelect':
            return (React.createElement(TreeMultiSelectFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, options: filter.options, placeholder: filter.placeholder, disabled: disabled }));
        case 'dateRange':
            return (React.createElement(DateRangeFilter, { field: filter.field, label: filter.label, value: filter.value, onChange: filter.onChange, presets: filter.presets, resetOnCustomRange: filter.resetOnCustomRange, selectedPresetId: filter.selectedPresetId, onPresetSelect: filter.onPresetSelect, disabled: disabled }));
        default:
            return assertNever(filter);
    }
}
/**
 * FilterBar Shell (container organism)
 * Phase-1 implementation: renders optional search input, fixed "Filter:" label,
 * stubbed filters row, and trailing FilterToggle.
 * Concrete filter molecules are wired in task-07 (phase-3).
 *
 * Layout order: Search? | "Filter:" label | active filters | [actions: FilterToggle, Reset, Reset Cache]
 * The __actions cluster uses flex:0 0 auto so it never pushes filters off-screen.
 * @version 1.1.0
 * @since 2026-05-04
 */
export function FilterBar({ filters, onResetFilters, toggleDisabled = false, showSearch = false, searchValue = '', onSearchChange, searchPlaceholder, searchId = 'filter-bar-search', onFilterToggle, allowResetCache = false, onResetCache, children }) {
    const t = {
        searchLabel: translateWithFallback('search_label', 'Search'),
        searchPlaceholder: searchPlaceholder !== null && searchPlaceholder !== void 0 ? searchPlaceholder : translateWithFallback('search_placeholder', 'Search'),
        filterLabel: translateWithFallback('filter_label', 'Filter:'),
        resetLabel: translateWithFallback('reset_label', 'Reset'),
        resetCacheLabel: translateWithFallback('reset_cache_label', 'Reset Cache')
    };
    const visibleFilters = filters.filter(isFilterVisible);
    const toggleItems = filters.map((f) => {
        var _a, _b;
        // Phase-2/3 variants may add `active` and `locked` — cast safely for future proofing
        const fx = f;
        return {
            field: f.field,
            label: f.label,
            active: (_a = fx.active) !== null && _a !== void 0 ? _a : true,
            locked: (_b = fx.locked) !== null && _b !== void 0 ? _b : false,
            onToggle: () => onFilterToggle === null || onFilterToggle === void 0 ? void 0 : onFilterToggle(f.field)
        };
    });
    const showActions = (!toggleDisabled && onFilterToggle) || onResetFilters || (allowResetCache && onResetCache);
    return (React.createElement("article", { className: "qmb-ui-filter-bar", "aria-label": "Filter bar" },
        showSearch && (React.createElement("div", { className: "qmb-ui-filter-bar__search" },
            React.createElement("input", { id: searchId, type: "search", className: "qmb-ui-filter-bar__search-input", value: searchValue, placeholder: t.searchPlaceholder, "aria-label": t.searchLabel, onChange: (e) => onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(e.currentTarget.value) }),
            React.createElement("i", { className: "fa-light fa-magnifying-glass", "aria-hidden": "true" }))),
        React.createElement("span", { className: "qmb-ui-filter-bar__label" }, t.filterLabel),
        React.createElement("div", { className: "qmb-ui-filter-bar__filters", role: "group", "aria-label": "Active filters" }, visibleFilters.map((filter) => (React.createElement("div", { key: filter.field, className: "qmb-ui-filter-bar__option" }, renderFilter(filter))))),
        showActions && (React.createElement("div", { className: "qmb-ui-filter-bar__actions" },
            !toggleDisabled && onFilterToggle && React.createElement(FilterToggle, { items: toggleItems }),
            onResetFilters && (React.createElement(Button, { type: "button", variant: "highlighted", onClick: onResetFilters },
                React.createElement("i", { className: "fa-light fa-arrow-rotate-left", "aria-hidden": "true" }),
                t.resetLabel)),
            allowResetCache && onResetCache && (React.createElement(Button, { type: "button", variant: "highlighted", onClick: onResetCache }, t.resetCacheLabel)))),
        children));
}
